import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Drawer,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from 'react-oidc-context';
import CreateApi from '../Settings/appSettings';
import { buildFirstTeeEventScores } from '@moment-golf/shared';
import type { SelectedPlayer } from '@moment-golf/shared';

export interface FirstTeeDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Full event document — must include groupSelfSelection, teeSelfSelection, regPerTeeTime */
    eventData: any;
    /** Full course document — teeSets used to populate tee set dropdowns */
    courseData: any;
    /** Pre-assigned tee time group; used when groupSelfSelection is off (teeSelfSelection-only mode) */
    registrationData: any[];
    currentRound: number;
    currentTeeTime: string | Date;
    startingHole: number;
    /** Scorer's own scoreCode */
    scoreCode: string;
    /** Called after scores are created — triggers fetchScoringData in App.tsx */
    onComplete: () => void;
}

export const FirstTeeDrawer = ({
    open,
    onClose,
    eventData,
    courseData,
    registrationData,
    currentRound,
    currentTeeTime,
    startingHole,
    scoreCode,
    onComplete,
}: FirstTeeDrawerProps) => {
    const api = CreateApi();
    const auth = useAuth();

    const groupSelfSelection: boolean = !!eventData?.groupSelfSelection;
    const teeSelfSelection: boolean = !!eventData?.teeSelfSelection;
    const regPerTeeTime: number = Number(eventData?.regPerTeeTime) || 4;

    // Step -1 = scorer confirmation (groupSelfSelection only)
    // Step  0 = player selection    (groupSelfSelection path)
    // Step  1 = tee set selection   (teeSelfSelection path)
    const initialStep = groupSelfSelection ? -1 : 1;

    const [step, setStep] = useState(initialStep);
    const [unassignedRegs, setUnassignedRegs] = useState<any[]>([]);
    const [loadingRegs, setLoadingRegs] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedPlayers, setSelectedPlayers] = useState<SelectedPlayer[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const teeSetOptions: string[] = (courseData?.teeSets ?? [])
        .map((ts: any) => ts.teeName ?? ts.teeSet ?? '')
        .filter(Boolean);

    // Reset internal state each time the drawer opens
    useEffect(() => {
        if (!open) return;
        setStep(initialStep);
        setSearch('');
        setSubmitting(false);
        if (!groupSelfSelection) {
            setSelectedPlayers(
                (registrationData ?? []).map((r: any) => ({
                    scoreCode: r.scoreCode,
                    playerName: r.playerName ?? r.scoreCode,
                    teeSet: r.teeSet ?? teeSetOptions[0] ?? '',
                    playerHandicap: r.playerHandicap ?? r.handicap ?? null,
                    gender: r.gender ?? 'mens',
                })),
            );
        } else {
            setSelectedPlayers([]);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch unassigned registrations when Step 0 is active
    useEffect(() => {
        if (!open || !groupSelfSelection || step !== 0) return;
        setLoadingRegs(true);
        api
            .get('/ls/GetUnassignedRegistrations', {
                params: { eventID: eventData?._id },
            })
            .then((res: any) => {
                const regs = res.data ?? [];
                setUnassignedRegs(regs);

                // Pre-select and lock the current scorer's own registration
                const ownReg = regs.find((r: any) => r.scoreCode === scoreCode);
                if (ownReg) {
                    setSelectedPlayers([{
                        scoreCode: ownReg.scoreCode,
                        playerName: ownReg.playerName ?? ownReg.scoreCode,
                        teeSet: ownReg.teeSet ?? teeSetOptions[0] ?? '',
                        playerHandicap: ownReg.playerHandicap ?? null,
                        gender: ownReg.gender ?? 'mens',
                    }]);
                }
            })
            .catch(() => setUnassignedRegs([]))
            .finally(() => setLoadingRegs(false));
    }, [open, step]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredRegs = unassignedRegs.filter(
        (r) => !search || (r.playerName ?? '').toLowerCase().includes(search.toLowerCase()),
    );

    const isChecked = (reg: any) =>
        selectedPlayers.some((p) => p.scoreCode === reg.scoreCode);

    const isOwn = (reg: any) => reg.scoreCode === scoreCode;

    const togglePlayer = (reg: any) => {
        if (isOwn(reg)) return; // own registration is locked
        if (isChecked(reg)) {
            setSelectedPlayers((prev) => prev.filter((p) => p.scoreCode !== reg.scoreCode));
        } else if (selectedPlayers.length < regPerTeeTime) {
            setSelectedPlayers((prev) => [
                ...prev,
                {
                    scoreCode: reg.scoreCode,
                    playerName: reg.playerName ?? reg.scoreCode,
                    teeSet: reg.teeSet ?? teeSetOptions[0] ?? '',
                    playerHandicap: reg.playerHandicap ?? null,
                    gender: reg.gender ?? 'mens',
                },
            ]);
        }
    };

    const updateTeeSet = (playerScoreCode: string, teeSet: string) => {
        setSelectedPlayers((prev) =>
            prev.map((p) => (p.scoreCode === playerScoreCode ? { ...p, teeSet } : p)),
        );
    };

    const handleConfirmGroup = () => {
        if (teeSelfSelection) {
            setStep(1);
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            if (groupSelfSelection) {
                const eventScores = buildFirstTeeEventScores({
                    selectedPlayers,
                    roundNumber: currentRound,
                    teeTime: currentTeeTime,
                    startingHole,
                    eventData,
                    courseData,
                    auth,
                    markerScoreCode: scoreCode,
                });

                await api.post('/tb/CreateEventScores', {
                    eventScores,
                    eventID: eventData?._id,
                    userID: auth.user?.profile?.sub,
                });

                if (teeSelfSelection) {
                    await api.post('/ls/UpdateRegistrationTeeSets', {
                        eventID: eventData?._id,
                        playerTeeSets: selectedPlayers.map((p) => ({
                            scoreCode: p.scoreCode,
                            teeSet: p.teeSet,
                        })),
                        userID: auth.user?.profile?.sub,
                    });
                }
            } else if (teeSelfSelection) {
                await api.post('/ls/ApplyTeeSetHandicapUpdate', {
                    eventID: eventData?._id,
                    roundNumber: currentRound,
                    playerTeeSets: selectedPlayers.map((p) => ({
                        scoreCode: p.scoreCode,
                        teeSet: p.teeSet,
                    })),
                    userID: auth.user?.profile?.sub,
                });
            }

            onComplete();
        } catch (err) {
            console.error('[FirstTeeDrawer] submit error', err);
        } finally {
            setSubmitting(false);
        }
    };

    const stepTitle =
        step === -1 ? 'Group Scorer Confirmation' :
        step === 0  ? 'Select Your Group' :
                      'Select Tee Sets';

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    height: '90dvh',
                    borderRadius: '16px 16px 0 0',
                    backgroundColor: 'sepia.background',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, pt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}
            >
                <Typography variant="h6" fontWeight={600} color="primary.main">
                    {stepTitle}
                </Typography>
                <Button onClick={onClose} color="inherit" size="small" disabled={submitting}>
                    Cancel
                </Button>
            </Stack>

            {/* Fixed search bar — only visible on player selection step */}
            {step === 0 && (
                <Box sx={{ px: 2, pt: 1, pb: 0.5, flexShrink: 0 }}>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                        Select up to {regPerTeeTime} players for this group.
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>
            )}

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1 }}>

                {/* ── Step -1: Scorer Confirmation ── */}
                {step === -1 && (
                    <Stack direction="column" spacing={2} sx={{ pt: 2 }}>
                        <Typography variant="body1" color="text.secondary">
                            Are you going to score this group?
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => setStep(0)}
                            sx={{ backgroundColor: 'primary.light', color: 'white', '&:hover': { backgroundColor: 'primary.main' } }}
                        >
                            I will score
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onClose}
                            sx={{ backgroundColor: 'sepia.extraLight', color: 'sepia.dark', '&:hover': { backgroundColor: 'sepia.light', color: 'white' } }}
                        >
                            Naw, view only
                        </Button>
                    </Stack>
                )}

                {/* ── Step 0: Player Selection ── */}
                {step === 0 && (
                    <>
                        {loadingRegs ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Stack direction="column" spacing={0.5}>
                                {filteredRegs.map((reg) => {
                                    const checked = isChecked(reg);
                                    const own = isOwn(reg);
                                    const atLimit = !checked && !own && selectedPlayers.length >= regPerTeeTime;
                                    return (
                                        <Stack
                                            key={reg.scoreCode}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            onClick={() => !atLimit && !own && togglePlayer(reg)}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                cursor: atLimit || own ? 'default' : 'pointer',
                                                opacity: atLimit ? 0.5 : 1,
                                                '&:hover': {
                                                    backgroundColor: atLimit || own ? 'transparent' : 'action.hover',
                                                },
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                disabled={atLimit || own}
                                                onChange={() => togglePlayer(reg)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <Stack direction="column">
                                                <Typography variant="body1" fontWeight={own ? 700 : 500}>
                                                    {reg.playerName ?? reg.scoreCode}
                                                    {own && (
                                                        <Typography component="span" variant="caption" color="primary.main" ml={0.5}>
                                                            (you)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                {reg.teeSet && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {reg.teeSet}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Stack>
                                    );
                                })}
                                {filteredRegs.length === 0 && (
                                    <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        textAlign="center"
                                        py={4}
                                    >
                                        No unassigned players found.
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </>
                )}

                {/* ── Step 1: Tee Set Selection ── */}
                {step === 1 && (
                    <>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Select a tee set for each player.
                        </Typography>
                        <Stack direction="column" spacing={2}>
                            {selectedPlayers.map((player) => (
                                <Stack
                                    key={player.scoreCode}
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                                        {player.playerName}
                                    </Typography>
                                    <FormControl size="small" sx={{ flex: 1 }}>
                                        <InputLabel>Tee Set</InputLabel>
                                        <Select
                                            value={player.teeSet}
                                            label="Tee Set"
                                            onChange={(e) =>
                                                updateTeeSet(player.scoreCode, e.target.value as string)
                                            }
                                        >
                                            {teeSetOptions.map((ts) => (
                                                <MenuItem key={ts} value={ts}>
                                                    {ts}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            ))}
                        </Stack>
                    </>
                )}
            </Box>

            {/* Footer actions */}
            {step !== -1 && (
                <Stack
                    sx={{ px: 2, py: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                    spacing={1}
                >
                    {step === 0 ? (
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={selectedPlayers.length === 0 || submitting}
                            onClick={handleConfirmGroup}
                        >
                            Confirm Group ({selectedPlayers.length}/{regPerTeeTime})
                        </Button>
                    ) : (
                        <>
                            {groupSelfSelection && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => setStep(0)}
                                    disabled={submitting}
                                >
                                    Back
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                fullWidth
                                disabled={selectedPlayers.length === 0 || submitting}
                                onClick={handleSubmit}
                            >
                                {submitting ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    'Confirm & Start Scoring'
                                )}
                            </Button>
                        </>
                    )}
                </Stack>
            )}
        </Drawer>
    );
};
